import { COUNT_GROUPS, groupTotal, totalProjects } from '@/shared/lib/statusCounts';
import { cn } from '@/shared/lib/utils';
import { formatNumber, pluralize } from '@/shared/lib/format';
import type { StatusCounts as Counts } from '@/shared/types/domain';

/**
 * Contagem por situação, nas duas formas em que ela aparece no painel.
 *
 * Grupo com zero **não é renderizado**. Um painel de voluntário com cinco
 * caixas escritas "0" comunica um sistema vazio; três caixas com números
 * comunicam o que está acontecendo. Quem quiser o zero explícito olha o
 * filtro da listagem, onde ele é uma escolha.
 */

interface StatusTilesProps {
  counts: Counts;
  className?: string;
}

/** Forma grande: cabeçalho de uma tela de panorama (home, coordenação, ministério). */
export function StatusTiles({ counts, className }: StatusTilesProps) {
  const groups = COUNT_GROUPS.map((group) => ({
    ...group,
    total: groupTotal(counts, group.statuses),
  })).filter((group) => group.total > 0);

  if (groups.length === 0) return null;

  return (
    <dl
      className={cn(
        'stagger grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5',
        className,
      )}
    >
      {groups.map((group) => (
        <div key={group.key} className="rounded-lg border border-border bg-card p-4">
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {group.label}
          </dt>
          <dd className="tabular mt-1 text-2xl font-semibold">{formatNumber(group.total)}</dd>
        </div>
      ))}
    </dl>
  );
}

interface StatusPillsProps {
  counts: Counts;
  className?: string;
}

/**
 * Forma compacta: uma linha dentro de um card de coordenação ou de ministério.
 * Cabe em 375px porque quebra em várias linhas em vez de rolar.
 */
export function StatusPills({ counts, className }: StatusPillsProps) {
  const total = totalProjects(counts);

  if (total === 0) {
    // "Nenhum projeto visível para você", e não "nenhum projeto registrado":
    // um líder enxerga o NOME dos ministérios irmãos da coordenação dele, mas
    // não os projetos deles. Afirmar que o ministério vizinho não tem projeto
    // nenhum seria mentir — a contagem vem recortada pela RLS, como tudo.
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        Nenhum projeto visível para você
      </p>
    );
  }

  const groups = COUNT_GROUPS.map((group) => ({
    ...group,
    total: groupTotal(counts, group.statuses),
  })).filter((group) => group.total > 0);

  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-sm', className)}>
      <span className="font-medium">{pluralize(total, 'projeto', 'projetos')}</span>
      <span aria-hidden="true" className="text-border">
        ·
      </span>
      {groups.map((group, index) => (
        <span key={group.key} className="flex items-center gap-3 text-muted-foreground">
          <span>
            <span className="tabular font-medium text-foreground">{group.total}</span>{' '}
            {group.label.toLocaleLowerCase('pt-BR')}
          </span>
          {index < groups.length - 1 ? (
            <span aria-hidden="true" className="text-border">
              ·
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}
